import { useCallback, useEffect, useMemo, useState } from 'react'
import type { JSX, SyntheticEvent } from 'react'
import { Link } from 'react-router'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { useBrand } from '@/brand/context'
import { formatDateTime, relativeTime } from '@/lib/format'
import { safeStorage } from '@/lib/storage'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Field } from '@/ui/Field'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

type Connection = Schema<'LinkedInConnectionOut'>
type Post = Schema<'PostOut'>
type PostState = Schema<'PostState'>

/** The tenant's time zone is the only one this product renders in (CLAUDE.md). */
const IST = 'Asia/Kolkata'

/**
 * `PublishRequest.commentary`'s ceiling in `api/app/v1/connections.py`, which is
 * LinkedIn's own. Mirrored rather than derived because OpenAPI describes the
 * bound and the generated client does not enforce it — and a draft that only
 * discovers the limit as a 422 has spent the tenant's typing, not their budget.
 */
const MAX_CHARACTERS = 3000

/** `DEFAULT_POSTS` and `MAX_POSTS` in `api/app/db/linkedin.py`, mirrored. */
const FIRST_PAGE = 50
const MAX_PAGE = 200

/**
 * Publish to LinkedIn — `design/screens/outreach.html#post`, audit row §A5,
 * over `POST` and `GET /api/v1/connections/linkedin/posts`.
 *
 * S-B17 shipped the queue, the cap, the pacing floor and the publisher on
 * 2026-08-05, and the only way to use any of it was an HTTP client. This is the
 * screen. **The `GET` did not exist before S-G9** and is part of this row rather
 * than a nicety: the endpoint queues a post and answers with a job id, so
 * without a list there is no way for the person who pressed the button to learn
 * what happened to it. `api/app/v1/connections.py` argues that at the route.
 *
 * WHY THERE IS NO SIDEBAR ENTRY
 * -------------------------------
 * S-D3 approved seven Outreach sections and *Publish to LinkedIn* is not one of
 * them, so it is a `SUBSCREENS` entry in `web/src/shell/navigation.ts` — the
 * same call S-G5 made for `/seo/publish-targets`, and for the same reason:
 * adding an eighth section is inventing information architecture nobody drew.
 * It is reached from the LinkedIn card in Settings → Connections, which is where
 * a tenant goes when they are thinking about their LinkedIn account.
 *
 * WHAT THE PROTOTYPE DRAWS AND THIS SCREEN DELIBERATELY DOES NOT
 * ---------------------------------------------------------------
 * Three controls, and each is answered differently.
 *
 * **Save draft (OUTR-103).** There is still no draft *store*: a post exists in
 * a textarea in this browser and in `public.jobs` once it is queued, and there
 * is no endpoint, table or column between them. So the button is not drawn —
 * one labelled *Save draft* over `localStorage` would promise the tenant's
 * phone and a colleague's screen, and deliver neither. What the button was
 * actually for is done instead: the box is kept in this browser as it is typed,
 * per tenant, and the card says in one line exactly how far that goes. SF-22
 * made the same call for the inbox and drew its button, because there the draft
 * belongs to a conversation somebody is about to click away from; here there is
 * one box on one screen and nothing to switch between.
 *
 * **When: Now / Tomorrow 10:30 / Pick a time.** Nothing schedules. `run_after`
 * on the queued job is computed by the pacing floor and deferred by the send
 * window (C1) — the *server* decides when a post goes out, and it will not
 * accept a time from the client. So the screen shows what the API actually
 * answers, which is the earliest moment the post may be attempted, and says
 * plainly that AugRank spaces them. That is more information than the prototype's
 * dropdown, not less; it is just not a control.
 *
 * **Reaction counts on the recent-posts list.** AugRank never reads them. The
 * publisher is a one-way `POST` to LinkedIn's Posts API and nothing polls the
 * share afterwards, so *41 reactions* would be a number this product made up.
 * This is the fourth §A row to meet the same rule — §A3's *30-day uptime*, §V2's
 * ₹4.20, §A4's *218 suppressed* — and the answer is the same one: show the fact
 * that exists, labelled as what it is.
 *
 * **"Kept as a draft" under a failed post (OUTR-105).** Nothing keeps it, for
 * the reason above — but the text is not lost either: it is on the row, in
 * `PostOut.commentary`, which is the only copy there is. So the row hands it
 * back with a press rather than telling somebody to retype it.
 *
 * WHY THE LIST IS RENDERED FOR A MEMBER AND THE COMPOSER IS NOT
 * --------------------------------------------------------------
 * `GET` is `tenant:read` and `POST` is `discovery:manage`, which is the split
 * the server draws and the reason it draws it: a member needs to know whether
 * the organisation's posts are going out — that is why they came here — without
 * being able to put something on the company's LinkedIn account. A screen that
 * refused a member outright would hide a read they are entitled to; one that
 * showed them a composer would hand them a 403 on the press.
 */
export function PublishPost(): JSX.Element {
  const { tenant, membership } = useActiveTenant()
  const mayPublish = may(membership, 'discovery:manage')
  const [limit, setLimit] = useState(FIRST_PAGE)
  const storage = useMemo(() => safeStorage(), [])
  const draftKey = `augrank.post.draft.${tenant.id}`
  // OUTR-103. The post being written, held here rather than inside the composer
  // for two reasons that arrived together: it survives this browser being closed
  // (below), and a failed post can be put back into it from the list beside it
  // (OUTR-105) without the list reaching inside another component's state.
  //
  // **Read once, on mount**, like the inbox's draft and for the same reason: an
  // effect that restored it whenever its dependencies changed would overwrite
  // whatever had been typed since, and the one thing a draft must never do is
  // destroy a newer one.
  const [commentary, setCommentary] = useState(() => storage.getItem(draftKey) ?? '')

  // …with one exception, and it is belt to the key's braces. The **key** is what
  // keeps two organisations' drafts apart in one browser's `localStorage`, and it
  // is load-bearing. This is the case a mount would miss if the active tenant
  // ever changed *underneath* this screen: today's only switcher is a link to
  // `/organisations`, so the route change unmounts and re-reads — but the draft
  // would otherwise be one in-shell switcher away from being filed under the
  // wrong organisation, and nothing about that failure would be visible.
  // Adjusted during render rather than in an effect, which is React's own
  // pattern for state that has to follow a prop: no frame is painted with the
  // previous tenant's draft.
  const [draftTenant, setDraftTenant] = useState(tenant.id)
  if (draftTenant !== tenant.id) {
    setDraftTenant(tenant.id)
    setCommentary(storage.getItem(`augrank.post.draft.${tenant.id}`) ?? '')
  }

  const write = useCallback(
    (value: string) => {
      setCommentary(value)
      // An emptied box is not a draft — removed rather than stored as `""`, so
      // the key does not outlive the post it was for.
      if (value.trim()) storage.setItem(draftKey, value)
      else storage.removeItem(draftKey)
    },
    [storage, draftKey],
  )

  const connection = useQuery(
    (signal) => call(() => api.GET('/api/v1/connections/linkedin', { signal })),
    [],
  )

  const posts = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/connections/linkedin/posts', {
          params: { query: { limit } },
          signal,
        }),
      ),
    [limit],
  )

  return (
    <div className="ar-stack ar-stack--loose">
      <div className="ar-row">
        <h1 className="ar-h1">Publish to LinkedIn</h1>
        <span className="app-spacer" />
        <Allowance connection={connection.state.data ?? null} />
      </div>

      <div className="app-post">
        <div className="ar-stack">
          {connection.state.status === 'loading' ? (
            <Loading label="Loading your LinkedIn connection" lines={5} />
          ) : connection.state.status === 'error' ? (
            <div className="ar-card">
              <div className="ar-card__body">
                <ErrorState failure={connection.state.failure} onRetry={connection.refetch} />
              </div>
            </div>
          ) : !mayPublish ? (
            <NotYours />
          ) : (
            <Composer
              connection={connection.state.data}
              commentary={commentary}
              onCommentary={write}
              onQueued={() => {
                connection.refetch()
                posts.refetch()
              }}
            />
          )}
        </div>

        <div className="ar-stack">
          <div className="ar-card">
            <div className="ar-card__header">
              <h2>Recent posts</h2>
            </div>
            <RecentPosts
              posts={posts}
              connection={connection.state.data ?? null}
              limit={limit}
              // OUTR-105. Only where there is a composer to copy into: a member
              // sees the failure and the reason, and no button that would put
              // text in a box they are not shown.
              onCopy={mayPublish ? write : null}
              // …and never one that would overwrite a post already being
              // written. Unavailable rather than absent: the thing standing in
              // the way is on this screen and the person can clear it.
              occupied={commentary.trim() !== ''}
              onShowMore={() => {
                setLimit(MAX_PAGE)
              }}
            />
          </div>

          <div className="ar-alert ar-alert--info">
            <span className="ar-alert__icon" aria-hidden="true">
              ◷
            </span>
            <div className="ar-alert__body">
              <strong>Publishing is not scraping</strong>
              This uses LinkedIn&rsquo;s own API with your consent, so it carries none of the
              account risk the search side does. It still counts against the daily cap, because a
              burst of posts looks automated whichever door it came through.
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* --- the day's allowance --------------------------------------------------------- */

/**
 * *18 of 20 posts used today* — and it is the counter the account-health screen
 * shows, not a second one.
 *
 * `posts_today` and `max_posts_per_day` both come from `GET
 * /connections/linkedin`, which counts `public.jobs` rather than holding a
 * column, so this badge and the health screen's allowance cannot drift apart.
 * Nothing is rendered until the connection has loaded: a badge that said *0 of
 * 20* for half a second and then corrected itself would be worse than a gap.
 */
function Allowance({ connection }: { connection: Connection | null }): JSX.Element | null {
  if (connection === null) return null
  const { posts_today: used, max_posts_per_day: cap, posts_remaining_today: left } = connection
  const tone = left === 0 ? 'danger' : left <= 2 ? 'warning' : 'neutral'

  // One string rather than three children: a badge assembled from several text
  // nodes cannot be found by the text it visibly says, which is how it reads to
  // a screen reader too.
  const mark = left === 0 ? '✕ ' : left <= 2 ? '⚠ ' : ''

  return (
    <span className={`ar-badge ar-badge--${tone}`}>
      {`${mark}${String(used)} of ${String(cap)} posts used today`}
    </span>
  )
}

/* --- what a member sees ---------------------------------------------------------- */

function NotYours(): JSX.Element {
  return (
    <div className="ar-card">
      <div className="ar-card__body">
        <Empty
          title="Only owners and admins can publish"
          body="Posting puts something on your organisation's LinkedIn account in its name, so it is restricted to the roles that can also connect and disconnect it. What has been published is listed beside this."
        />
      </div>
    </div>
  )
}

/* --- composing ------------------------------------------------------------------- */

/**
 * The compose card. One textarea, one author, and a button that **queues**.
 *
 * WHY THE IDEMPOTENCY KEY IS A FUNCTION OF THE DRAFT
 * ----------------------------------------------------
 * `Idempotency-Key` is required by the route, and the reason it gives is that
 * LinkedIn has no idempotency of its own and no API to delete a post: a retried
 * request AugRank could not recognise is a second post on somebody's profile.
 * So the key has to be stable across a retry of *the same* post and different
 * for a *different* one, and the rule that gets both is that **an edit starts a
 * new attempt**. Typing regenerates it; pressing Publish again after a failure
 * does not. A key held for the life of the component would make the second post
 * of the day a silent no-op; a key generated at submit time would make a
 * dropped connection a double post.
 *
 * `created: false` is the case that proves it works — the same key arriving
 * twice returns the job already queued under it, and the screen says so rather
 * than claiming a second post went out.
 */
function Composer({
  connection,
  commentary,
  onCommentary,
  onQueued,
}: {
  connection: Connection
  commentary: string
  onCommentary: (value: string) => void
  onQueued: () => void
}): JSX.Element {
  const brand = useBrand()
  const toast = useToast()
  const [author, setAuthor] = useState('')
  const [key, setKey] = useState(newKey)
  // **Whether an attempt is in the air, tracked apart from the mutation.**
  //
  // The mutation's own `pending` cannot answer this, and reading it here was a
  // double post waiting to happen: an edit *resets* the mutation — that is what
  // mints a new key — so a keystroke landing while the request was in flight put
  // the button back to idle with a fresh key on it. Two presses, two keys, two
  // posts on somebody's LinkedIn account, and there is no API to delete one.
  const [sending, setSending] = useState(false)
  // The press is what refuses an empty post — the button is enabled as the
  // drawing has it, and the field says why nothing went out.
  const [blank, setBlank] = useState(false)

  const publish = useMutation((body: Schema<'PublishRequest'>) =>
    call(() =>
      api.POST('/api/v1/connections/linkedin/posts', {
        params: { header: { 'Idempotency-Key': key } },
        body,
      }),
    ),
  )

  // A different draft is a different post — an effect now rather than a call in
  // the change handler, because the text is `PublishPost`'s since OUTR-103/105
  // and *Copy into a new post* changes it from outside this component. The rule
  // is about the draft, not about who typed it: whatever put new text in the box
  // has started a new attempt, and the key has to move with it.
  const resetPublish = publish.reset
  useEffect(() => {
    setKey(newKey())
    resetPublish()
  }, [commentary, resetPublish])

  if (!connection.connected) return <NotConnected connection={connection} />

  const characters = commentary.trim().length
  const spent = connection.posts_remaining_today === 0
  // `pages` is optional on the generated type — it has a `default_factory` on
  // the server, so OpenAPI marks it not-required — and the composer branches on
  // its length. Narrowed once here rather than at each of the two uses.
  const pages = connection.pages ?? []

  function edited(): void {
    setKey(newKey())
    publish.reset()
  }

  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    // Belt as well as braces: the button is disabled while `sending`, and a
    // form submits on Enter from a focused field as well as on a press.
    if (sending) return
    if (characters === 0) {
      setBlank(true)
      return
    }
    setSending(true)
    let result
    try {
      result = await publish.run({
        commentary: commentary.trim(),
        // `null` rather than `undefined`: the generated types are checked with
        // `exactOptionalPropertyTypes`, and the server reads null as "my own
        // profile" — which is what an empty selection means.
        page_urn: author || null,
      })
    } finally {
      setSending(false)
    }
    if (!result.ok) return

    onCommentary('')
    setKey(newKey())
    toast.show({
      kind: result.data.created ? 'success' : 'info',
      title: result.data.created ? 'Queued to go out' : 'That post was already queued',
      body: result.data.created
        ? `${brand.productName} will publish it from ${formatDateTime(result.data.run_after, IST)}. Posts are spaced through the day.`
        : 'Nothing was published twice — the post you sent is already on its way.',
    })
    onQueued()
  }

  return (
    <form
      className="ar-card"
      noValidate
      onSubmit={(event) => {
        void onSubmit(event)
      }}
    >
      <div className="ar-card__header">
        <h2>New post</h2>
      </div>
      <div className="ar-card__body ar-stack">
        <InlineError failure={publish.state.failure} />

        {/* The picker is always drawn (outreach.html#post). With no Pages
            granted it holds the one author there is, and the help says what the
            consent covers instead of claiming a page it does not. */}
        <Field
          label="Post as"
          help={
            pages.length > 0
              ? 'Both are granted by the same OAuth consent — w_member_social for the profile, w_organization_social for the page. Disconnecting LinkedIn in Settings → Connections removes both.'
              : 'Granted by the OAuth consent — w_member_social for the profile. This authorisation covers the profile only — a company Page needs LinkedIn to grant the application the Pages permission, which it does per application rather than per account.'
          }
        >
          {(aria) => (
            <select
              {...aria}
              className="ar-select"
              value={author}
              onChange={(event) => {
                setAuthor(event.target.value)
                edited()
              }}
            >
              <option value="">
                {connection.account_label ?? 'Your profile'} — personal profile
              </option>
              {pages.map((page) => (
                <option key={page.urn} value={page.urn}>
                  {page.name} — company page
                </option>
              ))}
            </select>
          )}
        </Field>

        <Field
          label="Post"
          required
          help={`${characters.toLocaleString('en-IN')} of ${MAX_CHARACTERS.toLocaleString('en-IN')} characters. LinkedIn's own limit.`}
          error={blank && characters === 0 ? 'Write the post before publishing.' : undefined}
        >
          {(aria) => (
            <textarea
              {...aria}
              className="ar-textarea app-post__body"
              value={commentary}
              maxLength={MAX_CHARACTERS}
              placeholder="Finished a 3BHK in Gachibowli this week — full modular kitchen, wardrobes and false ceilings in nine weeks."
              onChange={(event) => {
                setBlank(false)
                onCommentary(event.currentTarget.value)
              }}
            />
          )}
        </Field>

        <p className="ar-text-body-sm ar-muted">
          Publishing queues the post rather than sending it this second. {brand.productName} spaces
          posts through the day and holds them inside your send window, because{' '}
          {connection.max_posts_per_day} posts landing in a quarter of an hour is what makes an
          account look automated.
        </p>

        {/* OUTR-103. What the drawing's *Save draft* is actually for, said
            plainly rather than drawn as a button that would promise more than
            it does: this is kept in this browser, not on the account, so it is
            not on the tenant's phone and a colleague cannot pick it up. */}
        <span className="ar-subtle app-post__note">
          Kept in this browser as you type, so closing the tab does not lose it. It is not saved to
          your account until you publish.
        </span>
      </div>
      <div className="ar-card__footer">
        <Button tone="primary" type="submit" busy={sending} busyLabel="Queueing" disabled={spent}>
          Publish
        </Button>
        <span className="ar-card__spacer" />
        {/* OUTR-149. Caption, as `outreach.html:997` draws it. */}
        <span className="ar-subtle app-post__note">
          {spent
            ? 'No posts left today. The allowance resets at midnight IST.'
            : `${String(connection.posts_remaining_today)} posts left today`}
        </span>
      </div>
    </form>
  )
}

/**
 * The state a tenant is actually in before they have connected anything — and
 * the one they land back in when a sixty-day grant runs out.
 *
 * Three sentences apart rather than one, because *never connected*, *LinkedIn
 * interrupted this account* and *the authorisation expired* have three different
 * remedies and only the middle one is not a button press.
 */
function NotConnected({ connection }: { connection: Connection }): JSX.Element {
  const brand = useBrand()
  const body =
    connection.status === null
      ? `${brand.productName} publishes through LinkedIn’s own authorisation, which nobody has granted yet. Connecting takes one consent screen and about ten seconds.`
      : connection.paused
        ? `LinkedIn interrupted this account. Clear whatever it is asking for in your own browser, then resume the account in Settings — ${brand.productName} never retries through a checkpoint.`
        : 'LinkedIn is no longer accepting this authorisation. It lasts sixty days and cannot be renewed in the background, so reconnecting is how it comes back.'

  return (
    <div className="ar-card">
      <div className="ar-card__body">
        <Empty
          title="LinkedIn is not connected"
          body={body}
          action={
            <Link className="ar-btn ar-btn--primary ar-btn--sm" to="/settings/connections">
              Go to Connections
            </Link>
          }
        />
        {connection.detail !== null ? (
          <p className="ar-text-body-sm ar-muted">LinkedIn said: {connection.detail}</p>
        ) : null}
      </div>
    </div>
  )
}

/* --- the recent-posts list -------------------------------------------------------- */

function RecentPosts({
  posts,
  connection,
  limit,
  onCopy,
  occupied,
  onShowMore,
}: {
  posts: ReturnType<typeof useQuery<Schema<'PostListResponse'>>>
  connection: Connection | null
  limit: number
  /** OUTR-105. `null` for a member, who has no composer to copy into. */
  onCopy: ((text: string) => void) | null
  occupied: boolean
  onShowMore: () => void
}): JSX.Element {
  if (posts.state.status === 'loading') {
    return (
      <div className="ar-card__body">
        <Loading label="Loading your recent posts" lines={3} />
      </div>
    )
  }

  if (posts.state.status === 'error') {
    return (
      <div className="ar-card__body">
        <ErrorState failure={posts.state.failure} onRetry={posts.refetch} />
      </div>
    )
  }

  const rows = posts.state.data.posts

  if (rows.length === 0) {
    return (
      <div className="ar-card__body">
        <Empty
          title="Nothing posted yet"
          body="Everything you publish from here is listed with what became of it — queued, published, or failed and why. Write the first one beside this."
        />
      </div>
    )
  }

  // `LIMIT n` with no offset, so a full page is the only signal there may be
  // more — the same shape the suppression list uses, and it says why.
  const capped = rows.length >= limit

  return (
    <>
      <div className="ar-card__body ar-stack ar-stack--tight">
        {rows.map((post) => (
          <PostPanel
            key={post.job_id}
            post={post}
            connection={connection}
            onCopy={onCopy}
            occupied={occupied}
          />
        ))}
      </div>
      {capped ? (
        <div className="ar-card__footer">
          {limit < MAX_PAGE ? (
            <Button size="sm" onClick={onShowMore}>
              Show more
            </Button>
          ) : (
            <span className="ar-subtle ar-text-body-sm">Showing the {MAX_PAGE} most recent.</span>
          )}
        </div>
      ) : null}
    </>
  )
}

/**
 * One post, and what became of it.
 *
 * The heading is the post's own first line. There is no title on a LinkedIn
 * post — the prototype invents *"Gachibowli 3BHK — 9 weeks"* — and the first
 * line is what a person actually recognises their own post by.
 */
function PostPanel({
  post,
  connection,
  onCopy,
  occupied,
}: {
  post: Post
  connection: Connection | null
  onCopy: ((text: string) => void) | null
  occupied: boolean
}): JSX.Element {
  const state = STATES[post.state]
  const now = new Date()
  // Pulled out of the JSX because the generated type is `string | null |
  // undefined` — the field is optional on the schema — and narrowing it inline
  // leaves a template literal taking a possibly-undefined argument.
  const detail = post.detail ?? null

  return (
    <div className="ar-panel">
      <strong className="app-post__excerpt">{firstLine(post.commentary)}</strong>
      <span className="ar-subtle app-post__meta">
        {authorLabel(post, connection)} · {when(post, now)}
      </span>
      {post.state !== 'published' ? (
        <span className={`ar-badge ar-badge--${state.tone} app-post__state`}>{state.label}</span>
      ) : null}
      {detail !== null ? (
        <span className="ar-subtle app-post__meta">
          {post.state === 'failed'
            ? detail
            : `Attempt ${String(post.attempts)} of ${String(post.max_attempts)} did not go through: ${detail}`}
        </span>
      ) : null}
      {post.state === 'failed' ? (
        <span className="ar-subtle app-post__meta">
          {post.reason === 'challenge'
            ? 'The account was paused by a LinkedIn checkpoint. Nothing is retried through one — clear it, resume the account, then post again.'
            : 'It was not published and will not be retried. Put the text back in the box once whatever LinkedIn objected to is fixed.'}
        </span>
      ) : null}
      {/* OUTR-105. The drawing says a failed post is *"kept as a draft"*, and
          nothing keeps it: there is no draft store, and the only copy of the
          text is the one on this row. So the row hands it back. It is one press
          rather than a select-and-copy of an 80-character excerpt that is not
          the whole post — the full commentary is already on the response.

          Unavailable rather than absent while something is being written, since
          replacing it would destroy the only copy of *that*. */}
      {post.state === 'failed' && onCopy !== null ? (
        <Button
          size="sm"
          tone="ghost"
          disabled={occupied}
          unavailable
          onClick={() => {
            onCopy(post.commentary)
          }}
        >
          {occupied ? 'Clear the box to copy this back' : 'Copy into a new post'}
        </Button>
      ) : null}
    </div>
  )
}

/** `PostState`, in the tenant's words. The four the API answers. */
const STATES: Record<
  PostState,
  { tone: 'neutral' | 'info' | 'success' | 'danger'; label: string }
> = {
  queued: { tone: 'neutral', label: 'Queued' },
  publishing: { tone: 'info', label: 'Publishing now' },
  published: { tone: 'success', label: 'Published' },
  failed: { tone: 'danger', label: '✕ Failed' },
}

/**
 * The one time each state is actually about.
 *
 * A queued post's interesting moment is in the future and a published one's is
 * in the past, so a single column would be *"2 days ago"* for one and a lie for
 * the other. `run_after` is the earliest the queue may attempt it, which is what
 * the `POST` answered when the button was pressed.
 */
function when(post: Post, now: Date): string {
  if (post.published_at !== null && post.published_at !== undefined) {
    return `published ${relativeTime(post.published_at, now)}`
  }
  if (post.state === 'queued') return `goes out from ${formatDateTime(post.run_after, IST)}`
  if (post.state === 'publishing') return 'going out now'
  return `tried ${relativeTime(post.run_after, now)}`
}

/**
 * Whose account it went to, resolved against the connection's own Pages.
 *
 * The API returns the author's URN rather than a label, because the Pages are
 * already on the connection this screen reads — composing the label on the
 * server would be a second answer to the same question, free to disagree with
 * the picker in the composer.
 */
function authorLabel(post: Post, connection: Connection | null): string {
  if (post.author_kind !== 'organization') return 'Profile'
  const page = (connection?.pages ?? []).find((candidate) => candidate.urn === post.author_urn)
  return page === undefined ? 'Company page' : `${page.name} · company page`
}

/** The post's first non-empty line, trimmed to something a card can hold. */
function firstLine(commentary: string): string {
  const line = commentary.split('\n').find((candidate) => candidate.trim() !== '') ?? commentary
  const trimmed = line.trim()
  return trimmed.length > 80 ? `${trimmed.slice(0, 79)}…` : trimmed
}

/**
 * A fresh `Idempotency-Key`.
 *
 * `crypto.randomUUID` rather than a counter or a timestamp: two tabs open on
 * this screen must not produce the same key, and a timestamp at second
 * resolution can. It is 36 characters, inside the route's 8–120.
 */
function newKey(): string {
  return crypto.randomUUID()
}
